import { Link } from 'react-router-dom';
import Layout from '@/components/feature/Layout';
import { siteConfig, categories } from '@/mocks/siteData';
import { getFeaturedComics, getHotComics, getLatestUpdates, getComicsByCategory, ComicArticle } from '@/mocks/comics';

function ComicCard({ comic }: { comic: ComicArticle }) {
  return (
    <Link
      to={`/article/${comic.slug}`}
      className="group block bg-background-50 rounded-lg border border-background-200/70 overflow-hidden hover:border-primary-300/50 transition-all duration-300 cursor-pointer"
    >
      <div className="relative aspect-[3/4] overflow-hidden">
        <img
          src={comic.coverImage}
          alt={`${comic.title} - 韩国漫画`}
          title={`${comic.title} - 好看的韩国漫画免费在线观看`}
          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
        />
        <div className="absolute top-2 left-2">
          <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
            comic.status === '连载中'
              ? 'bg-accent-500 text-background-50'
              : 'bg-secondary-500 text-background-50'
          }`}>
            {comic.status}
          </span>
        </div>
        <div className="absolute top-2 right-2 flex items-center gap-1 bg-background-950/70 text-background-50 text-xs px-2 py-0.5 rounded-full">
          <i className="ri-star-fill text-secondary-400 text-xs"></i>
          <span>{comic.rating}</span>
        </div>
      </div>
      <div className="p-3">
        <h3 className="text-sm font-semibold text-foreground-900 group-hover:text-primary-600 transition-colors line-clamp-1 font-heading">
          {comic.title}
        </h3>
        <p className="text-xs text-foreground-500 mt-1 line-clamp-1">{comic.author}</p>
        <div className="flex items-center justify-between mt-2">
          <span className="text-xs text-foreground-400">
            <i className="ri-eye-line mr-1"></i>
            {comic.views >= 10000 ? `${(comic.views / 10000).toFixed(1)}万` : comic.views}
          </span>
          <span className="text-xs text-foreground-400">
            {comic.chapters}话
          </span>
        </div>
      </div>
    </Link>
  );
}

function SectionHeader({ title, subtitle, href }: { title: string; subtitle?: string; href?: string }) {
  return (
    <div className="flex items-end justify-between mb-6">
      <div>
        <h2 className="text-xl md:text-2xl font-bold text-foreground-900 font-heading">{title}</h2>
        {subtitle && <p className="text-sm text-foreground-500 mt-1">{subtitle}</p>}
      </div>
      {href && (
        <Link
          to={href}
          className="flex items-center gap-1 text-sm text-primary-600 hover:text-primary-700 font-medium transition-colors whitespace-nowrap"
        >
          查看全部
          <i className="ri-arrow-right-line text-sm"></i>
        </Link>
      )}
    </div>
  );
}

function HeroSection() {
  return (
    <section className="relative w-full h-[420px] md:h-[560px] flex items-center justify-center overflow-hidden">
      <img
        src="https://readdy.ai/api/search-image?query=Korean%20manhwa%20art%20style%20collage%20wallpaper%2C%20various%20comic%20characters%20dynamic%20poses%2C%20warm%20golden%20and%20crimson%20color%20palette%2C%20abstract%20geometric%20shapes%2C%20modern%20minimalist%20composition%2C%20clean%20artistic%20background%2C%20professional%20illustration%2C%20soft%20gradients&width=1600&height=800&seq=hero-bg-hanman&orientation=landscape"
        alt="韩漫网 - 韩国漫画大全"
        className="absolute inset-0 w-full h-full object-cover object-center"
      />
      <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/35 to-black/60"></div>
      <div className="relative z-10 w-full px-4 md:px-6 text-center max-w-4xl mx-auto">
        <h1 className="text-3xl md:text-5xl lg:text-6xl font-bold text-white font-heading leading-tight">
          <span className="block">探索最精彩的</span>
          <span className="block mt-2 text-secondary-300">韩国漫画世界</span>
        </h1>
        <p className="mt-4 md:mt-6 text-sm md:text-base text-white/70 max-w-2xl mx-auto leading-relaxed">
          韩漫网为您精选海量优质韩国漫画，涵盖恋爱、奇幻、动作、搞笑等多种类型。界面简洁，阅读流畅，
          <strong className="text-white/90 font-medium">全部内容免费在线观看</strong>
        </p>
        <div className="mt-6 md:mt-8 flex flex-col sm:flex-row items-center justify-center gap-3">
          <Link
            to="/category/all"
            className="px-8 py-3 bg-primary-500 text-background-50 rounded-full text-sm md:text-base font-medium hover:bg-primary-600 transition-all duration-300 whitespace-nowrap cursor-pointer shadow-lg shadow-primary-500/25"
          >
            立即开始阅读
            <i className="ri-arrow-right-line ml-2"></i>
          </Link>
          <Link
            to="/category/romance"
            className="px-8 py-3 border border-white/30 text-white rounded-full text-sm md:text-base font-medium hover:bg-white/10 transition-all duration-300 whitespace-nowrap cursor-pointer"
          >
            热门推荐
          </Link>
        </div>
        <div className="mt-8 flex items-center justify-center gap-6 text-white/60 text-xs">
          <span><strong className="text-white/90 text-base font-bold">5000+</strong> 部漫画</span>
          <span className="w-px h-4 bg-white/20"></span>
          <span><strong className="text-white/90 text-base font-bold">200万+</strong> 读者</span>
          <span className="w-px h-4 bg-white/20"></span>
          <span><strong className="text-white/90 text-base font-bold">每日更新</strong></span>
        </div>
      </div>
    </section>
  );
}

function CategoryNav() {
  return (
    <section className="max-w-7xl mx-auto px-4 md:px-6 py-8 md:py-10">
      <SectionHeader title="分类浏览" subtitle="发现你喜欢的漫画类型" href="/category/all" />
      <div className="grid grid-cols-5 md:grid-cols-5 lg:grid-cols-10 gap-2 md:gap-3">
        {categories.map((cat) => (
          <Link
            key={cat.slug}
            to={`/category/${cat.slug}`}
            className="flex flex-col items-center gap-2 p-3 md:p-4 rounded-lg bg-background-50 border border-background-200/70 hover:border-primary-300/50 hover:bg-primary-50/30 transition-all duration-300 cursor-pointer group"
          >
            <div className="w-10 h-10 md:w-12 md:h-12 flex items-center justify-center rounded-full bg-primary-50 group-hover:bg-primary-100 transition-colors">
              <i className={`${cat.icon} text-primary-600 text-lg md:text-xl`}></i>
            </div>
            <span className="text-xs md:text-sm text-foreground-700 font-medium whitespace-nowrap">{cat.name}</span>
          </Link>
        ))}
      </div>
    </section>
  );
}

function HotSection() {
  const hotComics = getHotComics();
  return (
    <section className="max-w-7xl mx-auto px-4 md:px-6 py-8 md:py-10">
      <div className="bg-background-100/70 rounded-xl p-4 md:p-6">
        <SectionHeader title="热门推荐" subtitle="读者最喜爱的韩漫作品" />
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3 md:gap-4">
          {hotComics.map((comic) => (
            <ComicCard key={comic.id} comic={comic} />
          ))}
        </div>
      </div>
    </section>
  );
}

function FeaturedSection() {
  const featuredComics = getFeaturedComics();

  return (
    <section className="max-w-7xl mx-auto px-4 md:px-6 py-8 md:py-10">
      <SectionHeader title="精选推荐" subtitle="编辑精选的高品质韩漫" />
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {featuredComics.map((comic, index) => (
          <Link
            key={comic.id}
            to={`/article/${comic.slug}`}
            className={`group relative rounded-xl overflow-hidden cursor-pointer ${
              index === 0 ? 'md:col-span-2 md:row-span-2' : ''
            }`}
          >
            <div className={`relative ${index === 0 ? 'aspect-[16/10]' : 'aspect-[3/4]'}`}>
              <img
                src={comic.coverImage}
                alt={`${comic.title} - 精选韩漫`}
                title={`${comic.title} - 好看的韩国漫画精选推荐`}
                className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent"></div>
              <div className="absolute bottom-0 left-0 right-0 p-4">
                <span className="inline-block px-2 py-0.5 rounded-full text-xs font-medium bg-accent-500 text-background-50 mb-2">
                  {comic.category}
                </span>
                <h3 className="text-white font-bold font-heading line-clamp-1">
                  {index === 0 ? (
                    <span className="text-lg md:text-xl">{comic.title}</span>
                  ) : (
                    <span className="text-sm">{comic.title}</span>
                  )}
                </h3>
                {index === 0 && (
                  <p className="text-white/70 text-sm mt-1 line-clamp-2">{comic.description}</p>
                )}
              </div>
            </div>
          </Link>
        ))}
      </div>
    </section>
  );
}

function LatestSection() {
  const latestComics = getLatestUpdates(10);
  return (
    <section className="max-w-7xl mx-auto px-4 md:px-6 py-8 md:py-10">
      <SectionHeader title="最近更新" subtitle="最新更新的漫画章节" href="/category/all" />
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3 md:gap-4">
        {latestComics.map((comic) => (
          <ComicCard key={comic.id} comic={comic} />
        ))}
      </div>
    </section>
  );
}

function CTASection() {
  return (
    <section className="max-w-7xl mx-auto px-4 md:px-6 py-10 md:py-14">
      <div className="relative rounded-2xl overflow-hidden bg-background-950 p-8 md:p-12 text-center">
        <img
          src="https://readdy.ai/api/search-image?query=Korean%20comic%20style%20abstract%20background%2C%20warm%20orange%20and%20golden%20gradient%2C%20soft%20brush%20strokes%2C%20geometric%20minimalist%20composition%2C%20artistic%20texture%2C%20clean%20modern%20design&width=1200&height=400&seq=cta-bg-hanman&orientation=landscape"
          alt=""
          className="absolute inset-0 w-full h-full object-cover opacity-30"
        />
        <div className="relative z-10">
          <h2 className="text-2xl md:text-3xl font-bold text-white font-heading">准备好开始你的韩漫之旅了吗？</h2>
          <p className="mt-3 text-white/60 text-sm max-w-lg mx-auto">
            数千部精选韩国漫画等你来发现，全部免费在线观看，随时随地畅享阅读乐趣！
          </p>
          <Link
            to="/category/all"
            className="inline-flex items-center gap-2 mt-6 px-8 py-3 bg-primary-500 text-background-50 rounded-full text-sm font-medium hover:bg-primary-600 transition-all cursor-pointer whitespace-nowrap"
          >
            立即探索
            <i className="ri-arrow-right-line"></i>
          </Link>
        </div>
      </div>
    </section>
  );
}

function FAQSection() {
  const faqs = [
    {
      q: '韩漫网上的漫画都是免费的吗？',
      a: '是的！韩漫网上的所有韩国漫画内容均免费提供在线观看，无需付费或订阅。我们致力于为广大漫画爱好者提供免费的优质阅读体验。',
    },
    {
      q: '韩漫网的漫画多久更新一次？',
      a: '韩漫网保持每日更新频率，每天都有超过100话的新内容上线。我们与多家韩国漫画平台保持同步更新，确保读者能第一时间看到最新的漫画内容。',
    },
    {
      q: '我可以在手机上阅读韩漫网的内容吗？',
      a: '当然可以！韩漫网采用响应式设计，完美适配手机、平板和电脑等各种设备。您可以在任何设备上流畅地阅读喜欢的韩国漫画。',
    },
    {
      q: '如何找到我喜欢的类型的漫画？',
      a: '韩漫网提供了详细的分类导航，包括恋爱、奇幻、动作、搞笑、悬疑等十多种类型。您可以通过分类页面按类型浏览，也可以使用搜索功能直接查找想看的漫画。',
    },
  ];

  return (
    <section className="max-w-7xl mx-auto px-4 md:px-6 py-8 md:py-10">
      <SectionHeader title="常见问题" subtitle="关于韩漫网的常见疑问解答" />
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-4xl mx-auto">
        {faqs.map((faq, i) => (
          <div key={i} className="p-5 rounded-lg bg-background-50 border border-background-200/70">
            <h3 className="text-sm font-semibold text-foreground-900 font-heading">{faq.q}</h3>
            <p className="text-sm text-foreground-600 mt-2 leading-relaxed">{faq.a}</p>
          </div>
        ))}
      </div>
    </section>
  );
}

export default function Home() {
  const homeSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: siteConfig.name,
    url: siteConfig.url,
    description: siteConfig.description,
    potentialAction: {
      '@type': 'SearchAction',
      target: `${siteConfig.url}/search?q={search_term_string}`,
      'query-input': 'required name=search_term_string',
    },
  };

  const orgSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: siteConfig.name,
    url: siteConfig.url,
    description: siteConfig.description,
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: '韩漫网上的漫画都是免费的吗？',
        acceptedAnswer: { '@type': 'Answer', text: '是的！韩漫网上的所有韩国漫画内容均免费提供在线观看，无需付费或订阅。' },
      },
      {
        '@type': 'Question',
        name: '韩漫网的漫画多久更新一次？',
        acceptedAnswer: { '@type': 'Answer', text: '韩漫网保持每日更新频率，每天都有超过100话的新内容上线。' },
      },
      {
        '@type': 'Question',
        name: '我可以在手机上阅读韩漫网的内容吗？',
        acceptedAnswer: { '@type': 'Answer', text: '当然可以！韩漫网采用响应式设计，完美适配手机、平板和电脑等各种设备。' },
      },
      {
        '@type': 'Question',
        name: '如何找到我喜欢的类型的漫画？',
        acceptedAnswer: { '@type': 'Answer', text: '韩漫网提供了详细的分类导航，包括恋爱、奇幻、动作、搞笑、悬疑等十多种类型。' },
      },
    ],
  };

  return (
    <Layout>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(homeSchema) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(orgSchema) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }} />

      <HeroSection />
      <CategoryNav />
      <HotSection />
      <FeaturedSection />
      <LatestSection />
      <CTASection />
      <FAQSection />
    </Layout>
  );
}